// PhotoPicker.jsx
// Used inside OwnerDashboard when adding/editing a catalogue item
// Two modes: "Pick Stock Photo" and "Upload My Own"

import { useState, useRef, useEffect } from "react";
import { findStockPhotos, STOCK_PHOTOS, CATEGORY_EMOJIS } from "../data/stockPhotos";
const API_BASE = "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod";

// Compress image client-side before upload (max 800x800, JPEG 80%)
async function compressImage(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const MAX = 800;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        if (width > height) {
          height = Math.round((height * MAX) / width);
          width = MAX;
        } else {
          width = Math.round((width * MAX) / height);
          height = MAX;
        }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.8);
    };
    img.src = url;
  });
}

export default function PhotoPicker({ itemName, itemId, businessId, currentPhoto, onPhotoSelected }) {
  const [mode, setMode] = useState("none"); // "none" | "stock" | "upload"
  const [query, setQuery] = useState(itemName || "");
  const [suggestions, setSuggestions] = useState([]);
  const [allPhotos, setAllPhotos] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [preview, setPreview] = useState(currentPhoto || null);
  const fileRef = useRef();

  // Auto-suggest when itemName changes
  useEffect(() => {
    if (itemName) {
      setQuery(itemName);
      const results = findStockPhotos(itemName);
      setSuggestions(results);
    }
  }, [itemName]);

  function handleSearch(e) {
    const val = e.target.value;
    setQuery(val);
    setSuggestions(findStockPhotos(val));
  }

  function selectStockPhoto(photo) {
    setPreview(photo.url);
    onPhotoSelected({ type: "stock", url: photo.url, photoId: photo.id });
    setMode("none");
  }

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError("");
    setUploading(true);

    try {
      // Compress first
      const compressed = await compressImage(file);
      const ext = "jpg";
      const fileType = "image/jpeg";

      // Get fresh Firebase token
      const phone = localStorage.getItem("qkard_phone");
      const res = await fetch(`${API_BASE}/upload/presign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, businessId, productId: "qkard", fileType, fileExt: ext, itemId }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Upload failed");
      }

      const { presignedUrl, publicUrl } = await res.json();

      // Upload directly to S3
      const uploadRes = await fetch(presignedUrl, {
        method: "PUT",
        headers: { "Content-Type": fileType },
        body: compressed,
      });

      if (!uploadRes.ok) throw new Error("S3 upload failed");

      // Add cache-bust to avoid stale image
      const finalUrl = `${publicUrl}?t=${Date.now()}`;
      setPreview(finalUrl);
      onPhotoSelected({ type: "upload", url: publicUrl });
      setMode("none");
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  }

  const displayPhotos = allPhotos ? STOCK_PHOTOS : suggestions.length > 0 ? suggestions : STOCK_PHOTOS.slice(0, 12);

  return (
    <div style={{ marginBottom: "16px" }}>
      {/* Current photo preview */}
      <div style={{ marginBottom: "10px" }}>
        {preview ? (
          <div style={{ position: "relative", display: "inline-block" }}>
            <img
              src={preview}
              alt="Item"
              style={{
                width: "100px",
                height: "100px",
                objectFit: "cover",
                borderRadius: "10px",
                border: "2px solid #f5c542",
                display: "block",
              }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
            <button
              onClick={() => { setPreview(null); onPhotoSelected(null); }}
              style={{
                position: "absolute",
                top: "-8px",
                right: "-8px",
                background: "#e74c3c",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                width: "22px",
                height: "22px",
                fontSize: "12px",
                cursor: "pointer",
                lineHeight: "22px",
                textAlign: "center",
              }}
            >
              ✕
            </button>
          </div>
        ) : (
          <div
            style={{
              width: "100px",
              height: "100px",
              borderRadius: "10px",
              border: "2px dashed #444",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "32px",
              background: "#1a1a24",
            }}
          >
            📦
          </div>
        )}
      </div>

      {/* Action buttons */}
      {mode === "none" && (
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button
            onClick={() => setMode("stock")}
            style={{
              padding: "7px 14px",
              background: "#1a1a24",
              border: "1px solid #f5c542",
              borderRadius: "8px",
              color: "#f5c542",
              fontSize: "13px",
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            📚 Pick Stock Photo
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            style={{
              padding: "7px 14px",
              background: "#1a1a24",
              border: "1px solid #555",
              borderRadius: "8px",
              color: "#ccc",
              fontSize: "13px",
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            📷 Upload My Own
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </div>
      )}

      {/* Upload status */}
      {uploading && (
        <p style={{ color: "#f5c542", fontSize: "13px", margin: "8px 0 0" }}>
          ⏳ Uploading...
        </p>
      )}
      {uploadError && (
        <p style={{ color: "#e74c3c", fontSize: "13px", margin: "8px 0 0" }}>
          ❌ {uploadError}
        </p>
      )}

      {/* Stock photo picker */}
      {mode === "stock" && (
        <div
          style={{
            background: "#0f0f18",
            border: "1px solid #333",
            borderRadius: "12px",
            padding: "14px",
            marginTop: "10px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
            <span style={{ color: "#f5c542", fontWeight: 600, fontSize: "14px" }}>
              📚 Stock Photos
            </span>
            <button
              onClick={() => setMode("none")}
              style={{
                background: "none",
                border: "none",
                color: "#888",
                cursor: "pointer",
                fontSize: "18px",
              }}
            >
              ✕
            </button>
          </div>

          {/* Search */}
          <input
            type="text"
            value={query}
            onChange={handleSearch}
            placeholder="Search e.g. milk, chawal, kurta..."
            style={{
              width: "100%",
              padding: "8px 12px",
              background: "#1a1a24",
              border: "1px solid #444",
              borderRadius: "8px",
              color: "#fff",
              fontSize: "14px",
              marginBottom: "12px",
              boxSizing: "border-box",
              fontFamily: "'DM Sans', sans-serif",
            }}
          />

          {/* Photo grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
              gap: "8px",
              maxHeight: "280px",
              overflowY: "auto",
            }}
          >
            {displayPhotos.map((photo) => (
              <button
                key={photo.id}
                onClick={() => selectStockPhoto(photo)}
                title={photo.name}
                style={{
                  background: "none",
                  border: "2px solid transparent",
                  borderRadius: "8px",
                  padding: "2px",
                  cursor: "pointer",
                  transition: "border-color 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#f5c542")}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "transparent")}
              >
                <img
                  src={photo.url}
                  alt={photo.name}
                  style={{
                    width: "100%",
                    aspectRatio: "1",
                    objectFit: "cover",
                    borderRadius: "6px",
                    display: "block",
                  }}
                  onError={(e) => {
                    e.target.parentElement.innerHTML = `<div style="width:100%;aspect-ratio:1;display:flex;align-items:center;justify-content:center;font-size:28px;background:#1a1a24;border-radius:6px">${photo.emoji}</div>`;
                  }}
                />
                <p
                  style={{
                    color: "#aaa",
                    fontSize: "10px",
                    margin: "3px 0 0",
                    textAlign: "center",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {photo.name}
                </p>
              </button>
            ))}
          </div>

          {/* Show all toggle */}
          {!allPhotos && suggestions.length > 0 && (
            <button
              onClick={() => setAllPhotos(true)}
              style={{
                marginTop: "10px",
                background: "none",
                border: "1px solid #444",
                borderRadius: "6px",
                color: "#888",
                fontSize: "12px",
                padding: "5px 12px",
                cursor: "pointer",
                width: "100%",
              }}
            >
              Browse all photos →
            </button>
          )}
        </div>
      )}
    </div>
  );
}
